import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { createApiKey } from 'shared/utils/apiUtil';
import { useOnboarding } from 'app/hooks/react-query/useOnboarding';
import { ONBOARDING_FIELDS, ONBOARDING_TYPES } from 'app/containers/Onboarding/utils/OnboardingConstants';
import { Routes } from 'shared/constants/routes';
import { useRootStore } from 'app/contexts/RootStoreContext';
import { CLOUD_TYPE_IDS } from 'users/constants/usersConstants';
import {
  AZURE_ONBOARDING_FIELDS,
  AZURE_ONBOARDING_STEP_TO_FIELDS,
  AZURE_ONBOARDING_STEPS,
} from '../AzureOnboardingConstants';
import * as AzureOnboardingUtils from '../AzureOnboardingUtils';
import { getAzureAccountTypeServer } from '../AzureOnboardingUtils';
import { useNavigate, useLocation } from 'react-router';
import { AWS_ONBOARDING_FIELDS } from 'app/containers/Onboarding/AwsOnboarding/AwsOnboardingConstants';
import { generateAccountId } from 'app/containers/Onboarding/utils/OnboardingUtils';

export default function useFormNavigation({ screen, screens }) {
  const { trigger, formState, reset, setValue, getValues, setError } = useFormContext();
  const { errors } = formState || {};
  const { usersStore } = useRootStore();
  const triggerErrors = async () => trigger(AZURE_ONBOARDING_STEP_TO_FIELDS[screen] || [], { shouldFocus: true });
  const { startOnboarding, updateOnboardingParams, cancelOnboarding, startOnboardingValidation } = useOnboarding();
  const { mutateAsync: handleStartOnboarding } = startOnboarding({
    onError: (error) => {
      setError(AZURE_ONBOARDING_FIELDS.APPLICATION_ID, {
        type: 'custom',
        message: error?.response?.data?.clientMessage || 'Something went wrong',
      });
    },
  });
  const onboardingType = getValues(AZURE_ONBOARDING_FIELDS.ONBOARDING_TYPE);
  const { mutateAsync: handleUpdateOnboardingParams } = updateOnboardingParams();
  const { mutateAsync: handleValidateOnboarding } = startOnboardingValidation();
  const { mutateAsync: handleCancelOnboarding } = cancelOnboarding();
  useWatch(AZURE_ONBOARDING_FIELDS.ONBOARDING_TYPE);
  useWatch(AZURE_ONBOARDING_FIELDS.AZURE_ACCOUNT_TYPE);
  const navigate = useNavigate();
  const location = useLocation();

  const nextStep = useCallback(async () => {
    const isValid = await triggerErrors();
    if (!isValid) {
      return;
    }
    const params = {
      userKey: getValues(AZURE_ONBOARDING_FIELDS.USER_KEY),
      apiKey: createApiKey(usersStore, true),
      cloudTypeId: CLOUD_TYPE_IDS.AZURE,
      applicationId: getValues(AZURE_ONBOARDING_FIELDS.APPLICATION_ID),
      directoryId: getValues(AZURE_ONBOARDING_FIELDS.DIRECTORY_ID),
      clientSecret: getValues(AZURE_ONBOARDING_FIELDS.CLIENT_SECRET),
      applicationDisplayName: getValues(AZURE_ONBOARDING_FIELDS.APPLICATION_DISPLAY_NAME),
      azureAccountType: getAzureAccountTypeServer(getValues(AZURE_ONBOARDING_FIELDS.AZURE_ACCOUNT_TYPE)),
      accountStorage: getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_STORAGE),
      containerName: getValues(AZURE_ONBOARDING_FIELDS.CONTAINER_NAME),
      directoryPrefix: getValues(AZURE_ONBOARDING_FIELDS.DIRECTORY_PREFIX),
      isMultipleFiles: +getValues(AZURE_ONBOARDING_FIELDS.IS_MULTIPLE_FILES),
      filterOutMicrosoft365: +getValues(AZURE_ONBOARDING_FIELDS.FILTER_OUT_MICROSOFT_365),
      autoCreateCustomersProperty: getValues(AZURE_ONBOARDING_FIELDS.AUTO_CREATE_CUSTOMERS_PROPERTY),
      isMultipleSources: +getValues(AZURE_ONBOARDING_FIELDS.IS_MULTIPLE_SOURCES),
      providerCurrency: getValues(AZURE_ONBOARDING_FIELDS.PROVIDER_CURRENCY),
      processingCurrency: getValues(AZURE_ONBOARDING_FIELDS.PROCESSING_CURRENCY),
      accountKey: getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_KEY),
      accountId: getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_ID),
      accountName: getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_NAME),
      tenantName: getValues(AZURE_ONBOARDING_FIELDS.TENANT_NAME),
      tenantScheme: getValues(AZURE_ONBOARDING_FIELDS.TENANT_SCHEMA_NAME),
      resellerCustomer: getValues(AZURE_ONBOARDING_FIELDS.RESELLER_CUSTOMER),
      resellerCustomerId: getValues(AZURE_ONBOARDING_FIELDS.RESELLER_CUSTOMER_ID),
      resellerCustomerEmail: getValues(ONBOARDING_FIELDS.CUSTOMER_ADMIN_EMAIL),
      existingCustomerSelected: getValues(AWS_ONBOARDING_FIELDS.EXISTING_CUSTOMER_SELECTED),
      onboardingType: getValues(AZURE_ONBOARDING_FIELDS.ONBOARDING_TYPE),
      validationStarted: getValues(AZURE_ONBOARDING_FIELDS.VALIDATION_STARTED),
      accountType: getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_TYPE),
      excludedLinkedAccountMatch: getValues(AZURE_ONBOARDING_FIELDS.EXCLUDED_LINKED_ACCOUNT_MATCH),
      isCustomerSelfManaged: +getValues(AZURE_ONBOARDING_FIELDS.IS_CUSTOMER_SELF_MANAGED),
      isReseller: +getValues(AZURE_ONBOARDING_FIELDS.IS_MSP),
      autoAssignLinkedAccounts: +getValues(AZURE_ONBOARDING_FIELDS.AUTO_ASSIGN_LINKED_ACCOUNTS),
      [AZURE_ONBOARDING_FIELDS.IS_BIGQUERY]: +getValues(AZURE_ONBOARDING_FIELDS.IS_BIGQUERY),
      [AZURE_ONBOARDING_FIELDS.AZURE_COST_TYPE]: getValues(AZURE_ONBOARDING_FIELDS.AZURE_COST_TYPE),
      [AZURE_ONBOARDING_FIELDS.SKIP_DATA_SIMULATION]: !!getValues(AZURE_ONBOARDING_FIELDS.SKIP_DATA_SIMULATION),
      accountProperties: [
        {
          filterOutMicrosoft365: +getValues(AZURE_ONBOARDING_FIELDS.FILTER_OUT_MICROSOFT_365),
          autoCreateCustomersPropertyName: getValues(AZURE_ONBOARDING_FIELDS.AUTO_CREATE_CUSTOMERS_PROPERTY),
        },
      ],
    };
    if (screen === AZURE_ONBOARDING_STEPS.APP_REGISTRATION) {
      const searchParams = new URLSearchParams(location.search);
      const accountIdChanged =
        searchParams.get('accountId') &&
        getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_ID) !== searchParams.get('accountId');
      if (!params.accountId) {
        const newAccountId = generateAccountId(getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_NAME));
        setValue(AZURE_ONBOARDING_FIELDS.ACCOUNT_ID, newAccountId);
        params.accountId = newAccountId;
      }
      if (!getValues(AZURE_ONBOARDING_FIELDS.STARTED) || accountIdChanged) {
        if (accountIdChanged) {
          await handleCancelOnboarding({ accountId: searchParams.get('accountId') });
        }
        try {
          const response = await handleStartOnboarding(params);
          setValue(AWS_ONBOARDING_FIELDS.START_TIME, response.startTime);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.info(error);
          return;
        }
        setValue(AZURE_ONBOARDING_FIELDS.STARTED, true);
        const addQueryParam = (key, value) => {
          const url = new URL(window.location.href);
          url.searchParams.set(key, value);
          window.history.pushState(null, '', url.toString());
        };
        addQueryParam('accountId', params.accountId);
      } else {
        await handleUpdateOnboardingParams(params);
      }
    }

    if (screen === AZURE_ONBOARDING_STEPS.BILLING_EXPORT) {
      params[AZURE_ONBOARDING_FIELDS.VALIDATION_STARTED] = true;
      await handleUpdateOnboardingParams(params);
      await handleValidateOnboarding(params);
      setValue(AZURE_ONBOARDING_FIELDS.VALIDATION_STARTED, true);
      setValue(AZURE_ONBOARDING_FIELDS.VALIDATION_START_TRIGGER, true);
      return;
    }

    if (screen === AZURE_ONBOARDING_STEPS.VALIDATE_ACCOUNT) {
      await handleUpdateOnboardingParams({
        accountId: getValues(AZURE_ONBOARDING_FIELDS.ACCOUNT_ID),
        [AZURE_ONBOARDING_FIELDS.ACCOUNT_SETUP_SHOULD_START]: 1,
      });
      setValue(AZURE_ONBOARDING_FIELDS.ACCOUNT_SETUP_SHOULD_START, true);
    }

    // go to dashboard page if it is the last screen of onboarding (invoice is already ran, so it should have data)
    if (screens && screens.findIndex((scr) => scr.id === screen) === screens.length - 1) {
      navigate(Routes.DASHBOARD);
    }

    setValue(
      AZURE_ONBOARDING_FIELDS.SCREEN,
      AzureOnboardingUtils.handleNextScreen(
        screen,
        onboardingType === ONBOARDING_TYPES.AUTOMATIC,
        getValues(AZURE_ONBOARDING_FIELDS.AZURE_ACCOUNT_TYPE),
      ),
    );
  }, [errors, screen, onboardingType]);

  const prevStep = useCallback(() => {
    reset({}, { keepValues: true, keepTouched: false, keepErrors: false, keepDirty: true });

    if (screen === AZURE_ONBOARDING_STEPS.VALIDATE_ACCOUNT) {
      setValue(AZURE_ONBOARDING_FIELDS.VALIDATION_STARTED, false);
    }

    setValue(
      AZURE_ONBOARDING_FIELDS.SCREEN,
      AzureOnboardingUtils.handlePrevScreen(
        screen,
        onboardingType === ONBOARDING_TYPES.AUTOMATIC,
        getValues(AZURE_ONBOARDING_FIELDS.AZURE_ACCOUNT_TYPE),
      ),
    );
  }, [errors, screen, onboardingType]);

  return {
    nextStep,
    prevStep,
  };
}
